import React, { useState, useEffect } from "react";
import { FaMoneyBillWave, FaCheck } from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import "./Payment.css";
import { SiRazorpay } from "react-icons/si";
import { useLocation, useNavigate } from "react-router-dom";
import getCurrentUser from "./common/GetCurrentUser";
import { sendRequest } from "../ApiRequest";
import { BASE_URL, RAZORPAY_KEY } from "../URL/constants";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { loadRazorpay } from "../utils/razorpayLoader";
import { calculateCartTotal } from "../utils/priceUtils";
import OrderItemsSummary from "./common/OrderItemsSummary";

const Payment = () => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const { formData, onPaymentSuccess } = location.state || {};
  const { currentUser } = getCurrentUser();
  const reduxCartItems = useSelector((state) => state.addItem);
  const [cartItems, setCartItems] = useState([]);
  const [cartLoading, setCartLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  
  // Get cart items from multiple sources
  useEffect(() => {
    const fetchCartData = async () => {
      setCartLoading(true);
      try {
        let finalCartItems = [];
        
        // For logged-in users, ALWAYS use API cart as source of truth to avoid duplicates
        if (currentUser?.data?.customer) {
          try {
            const response = await sendRequest(
              'GET',
              `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart`,
              null,
              { Authorization: currentUser.data.token }
            );
            const apiCartItems = response.data.cart_items || [];
            
            // Fetch discount information for each cart item
            console.log('Payment - API cart items before discount fetch:', apiCartItems);
            const itemsWithDiscount = await Promise.all(
              apiCartItems.map(async (cartItem) => {
                try {
                  console.log('Payment - Fetching discount for item:', cartItem.item_id);
                  const itemResponse = await sendRequest(
                    'GET',
                    `${BASE_URL}/v1/public_site/menus/item_detail?id=${cartItem.item_id}`,
                    null,
                    { Authorization: currentUser.data.token }
                  );
                  const itemDetails = itemResponse.data.data;
                  console.log('Payment - Item details for', cartItem.item_id, ':', itemDetails);
                  const discount = itemDetails.sales_discount || 0;
                  console.log('Payment - Discount found:', discount);
                  return {
                    ...cartItem,
                    sales_discount: discount,
                    item_name: itemDetails.item_name,
                    item_code: itemDetails.item_code,
                    item_image_url: itemDetails.item_image_url,
                    unit_name: itemDetails.unit_name
                  };
                } catch (error) {
                  console.error('Payment - Error fetching discount:', error);
                  return {
                    ...cartItem,
                    sales_discount: 0
                  };
                }
              })
            );
            console.log('Payment - Items with discount:', itemsWithDiscount);
            finalCartItems = itemsWithDiscount;
          } catch (apiError) {
            console.error('API cart fetch failed:', apiError);
            // Only use Redux as fallback if API fails
            finalCartItems = reduxCartItems;
          }
        } else {
          // If not logged in, get from localStorage
          const guestCart = JSON.parse(localStorage.getItem("guestCart")) || {};
          const guestCartItems = Object.values(guestCart);
          // Deduplicate by item_id to avoid duplicates
          const uniqueItems = [];
          const seenItemIds = new Set();
          
          // First add guest cart items
          guestCartItems.forEach(item => {
            if (item.item_id && !seenItemIds.has(item.item_id)) {
              seenItemIds.add(item.item_id);
              uniqueItems.push(item);
            }
          });
          
          // Then add Redux items that aren't already present (by item_id)
          reduxCartItems.forEach(item => {
            if (item.item_id && !seenItemIds.has(item.item_id)) {
              seenItemIds.add(item.item_id);
              uniqueItems.push(item);
            }
          });
          
          // Fetch discount information for guest cart items
          const itemsWithDiscount = await Promise.all(
            uniqueItems.map(async (item) => {
              try {
                const response = await sendRequest('GET', `${BASE_URL}/v1/public_site/menus/item_detail?id=${item.item_id}`);
                return { ...item, sales_discount: response.data.data.sales_discount || 0 };
              } catch (error) {
                return { ...item, sales_discount: 0 };
              }
            })
          );
          finalCartItems = itemsWithDiscount;
        }
        
        // Get selected items from localStorage (saved by CheckoutHandler)
        const selectedItemIdsStr = localStorage.getItem('checkoutSelectedItems');
        if (selectedItemIdsStr) {
          try {
            const selectedItemIds = JSON.parse(selectedItemIdsStr);
            console.log('Payment - Selected item IDs from localStorage:', selectedItemIds);
            console.log('Payment - All cart items before filter:', finalCartItems.map(i => ({ id: i.id, item_id: i.item_id })));
            
            // Filter to only selected items
            // selectedItemIds could be either cart_item IDs (item.id) or product item IDs (item.item_id)
            finalCartItems = finalCartItems.filter(item => {
              const cartItemId = item.id; // cart_item ID
              const productItemId = item.item_id; // product item ID
              // Check if either cart_item ID or product item ID matches
              return selectedItemIds.some(selectedId => {
                const selectedIdNum = parseInt(selectedId);
                return selectedIdNum === cartItemId || selectedIdNum === productItemId || 
                       String(selectedId) === String(cartItemId) || String(selectedId) === String(productItemId);
              });
            });
            
            console.log('Payment - Filtered selected items:', finalCartItems.length);
          } catch (error) {
            console.error('Payment - Error parsing selected items:', error);
            // If error parsing, use all items
          }
        } else {
          console.log('Payment - No selected items in localStorage, using all items');
        }
        
        console.log('Payment - Final cart items to set:', finalCartItems);
        setCartItems(finalCartItems);
        
        // If we still don't have items and have Redux items, retry after a delay
        if (finalCartItems.length === 0 && reduxCartItems.length > 0 && retryCount < 3) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 2000); // Wait 2 seconds before retry
        }
      } catch (error) {
        // Final fallback to Redux state
        console.log('Final fallback to Redux state:', reduxCartItems);
        setCartItems(reduxCartItems);
      } finally {
        setCartLoading(false);
      }
    };

    fetchCartData();
  }, [currentUser, reduxCartItems, retryCount]);

  const totalAmount = calculateCartTotal(cartItems);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const paymentMethods = [
    {
      id: "cash",
      name: "Cash on Delivery",
      icon: <FaMoneyBillWave className="payment-icon cash" />,
      description: "Pay with cash when your order is delivered",
    },
    {
      id: "razorpay",
      name: "Razorpay",
      icon: <SiRazorpay className="payment-icon razorpay" />,
      description: "Secure payments via Razorpay gateway",
    },
  ];

  const clearCartAndRedirect = async () => {
    // NOTE: Don't clear cart items here - backend already deleted selected items during order creation
    // Only clear Redux state for selected items and localStorage
    
    // Clear selected items from localStorage
    localStorage.removeItem('checkoutSelectedItems');
    
    // Dispatch cart-updated event to refresh cart (remaining items will still be in cart)
    window.dispatchEvent(new Event('cart-updated'));
    
    if (onPaymentSuccess) {
      onPaymentSuccess();
    }
    navigate("/order");
  };

  const showSuccessMessage = () => {
    toast.success("Cash on Delivery successful! Your order has been placed.", {
      position: "top-center",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  // Function to verify payment status
  const verifyPaymentStatus = async (razorpayPaymentId, razorpayOrderId, orderId, retryCount = 0) => {
    const MAX_RETRIES = 5; // Maximum 5 retries (15 seconds total)
    
    try {
      const response = await sendRequest(
        'POST',
        `${BASE_URL}/v1/public_site/public_orders/verify_payment`,
        {
          razorpay_payment_id: razorpayPaymentId,
          razorpay_order_id: razorpayOrderId
        },
        { Authorization: currentUser.data.token }
      );

      if (response.data.success) {
        const { payment_status, order_status, message } = response.data;
        
        if (payment_status === 'success' && order_status === 'success') {
          toast.success("Payment verified successfully! Your order has been placed.");
          setTimeout(() => {
            clearCartAndRedirect();
          }, 2000);
          return true;
        } else if (payment_status === 'failed') {
          toast.error("Payment verification failed. Please contact support.");
          return false;
        } else if (payment_status === 'pending') {
          // Payment is still pending, check retry count
          if (retryCount < MAX_RETRIES) {
            toast.info(message || `Payment is being processed. Please wait... (${retryCount + 1}/${MAX_RETRIES})`);
            setTimeout(() => {
              verifyPaymentStatus(razorpayPaymentId, razorpayOrderId, orderId, retryCount + 1);
            }, 3000);
            return false;
          } else {
            // Max retries reached, try manual verification
            toast.info("Attempting manual payment verification...");
            const manualVerified = await manualVerifyPayment(razorpayPaymentId, razorpayOrderId);
            if (manualVerified) {
              return true;
            } else {
              // Manual verification also failed, show final message and redirect
              toast.info("Payment is being processed. You will receive confirmation shortly. Redirecting to orders page...");
              setTimeout(() => {
                clearCartAndRedirect();
              }, 2000);
              return false;
            }
          }
        } else {
          // Unknown status, check retry count
          if (retryCount < MAX_RETRIES) {
            toast.info("Payment is being processed. Please wait...");
            setTimeout(() => {
              verifyPaymentStatus(razorpayPaymentId, razorpayOrderId, orderId, retryCount + 1);
            }, 3000);
            return false;
          } else {
            // Max retries reached
            toast.info("Payment is being processed. You will receive confirmation shortly. Redirecting to orders page...");
            setTimeout(() => {
              clearCartAndRedirect();
            }, 2000);
            return false;
          }
        }
      } else {
        toast.error("Payment verification failed. Please contact support.");
        return false;
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      // If it's a 404 (payment not found), it might be a timing issue, retry once
      if (error.status === 404 && retryCount < MAX_RETRIES) {
        toast.info("Payment is being processed. Please wait...");
        setTimeout(() => {
          verifyPaymentStatus(razorpayPaymentId, razorpayOrderId, orderId, retryCount + 1);
        }, 3000);
        return false;
      }
      toast.error("Payment verification failed. Please contact support.");
      return false;
    }
  };

  // Function to manually verify payment with Razorpay API
  const manualVerifyPayment = async (razorpayPaymentId, razorpayOrderId) => {
    try {
      const response = await sendRequest(
        'POST',
        `${BASE_URL}/v1/public_site/public_orders/manual_verify_payment`,
        {
          razorpay_payment_id: razorpayPaymentId,
          razorpay_order_id: razorpayOrderId
        },
        { Authorization: currentUser.data.token }
      );

      if (response.data.success && response.data.payment_status === 'success') {
        toast.success("Payment verified successfully! Your order has been placed.");
        setTimeout(() => {
          clearCartAndRedirect();
        }, 2000);
        return true;
      } else {
        toast.info("Manual verification completed. Payment status: " + (response.data.payment_status || 'unknown'));
        setTimeout(() => {
          clearCartAndRedirect();
        }, 2000);
        return false;
      }
    } catch (error) {
      console.error('Manual payment verification error:', error);
      return false;
    }
  };

  const handlePayment = async () => {
    if (!selectedMethod) return;

    // Wait for cart data to load
    if (cartLoading) {
      toast.error("Please wait while we load your cart data.");
      return;
    }

    // Validate cart and total amount
    if (!cartItems || cartItems.length === 0) {
      toast.error("Your cart is empty. Please add items before proceeding.");
      return;
    }

    if (totalAmount <= 0) {
      toast.error("Invalid total amount. Please check your cart items.");
      return;
    }

    console.log("Cart items:", cartItems);
    console.log("Total amount:", totalAmount);

    setLoading(true);

    try {
      // Only update customer data if formData is provided
      if (formData) {
        await sendRequest(
          "PUT",
          `${BASE_URL}/v1/public_site/customers/${currentUser?.data?.customer?.id}`,
          {
            customer: {
              first_name: formData.firstName,
              last_name: formData.lastName,
              email: formData.email,
              phone: formData.phone,
              addresses_attributes: [
                {
                  ...(formData.shippingAddressId && { id: formData.shippingAddressId }),
                  address_type: "shipping",
                  address_line_1: formData.shippingAddressLine1,
                  address_line_2: formData.shippingAddressLine2,
                  city: formData.shippingCity,
                  state: formData.shippingState,
                  country: formData.shippingCountry,
                  postal_code: formData.shippingPincode,
                },
                {
                  ...(formData.billingAddressId && { id: formData.billingAddressId }),
                  address_type: "billing",
                  address_line_1: formData.billingAddressLine1,
                  address_line_2: formData.billingAddressLine2,
                  city: formData.billingCity,
                  state: formData.billingState,
                  country: formData.billingCountry,
                  postal_code: formData.billingPincode,
                },
              ],
            },
          },
          { Authorization: currentUser.data.token }
        );
      }

      if (selectedMethod === "cash") {
        // Get selected item IDs from localStorage
        const selectedItemIdsStr = localStorage.getItem('checkoutSelectedItems');
        const selectedItemIds = selectedItemIdsStr ? JSON.parse(selectedItemIdsStr) : null;
        
        await sendRequest(
          "POST",
          `${BASE_URL}/v1/public_site/public_orders`,
          {
            public_order: {
              total_amount: totalAmount,
              payment_method: "cash_on_delivery",
              status: "pending",
              selected_item_ids: selectedItemIds
            },
          },
          { Authorization: currentUser.data.token }
        );

        showSuccessMessage();
        setTimeout(() => {
          clearCartAndRedirect();
        }, 2000); 
      } else if (selectedMethod === "razorpay") {
        // Get selected item IDs from localStorage
        const selectedItemIdsStr = localStorage.getItem('checkoutSelectedItems');
        const selectedItemIds = selectedItemIdsStr ? JSON.parse(selectedItemIdsStr) : null;
        
        const orderResponse = await sendRequest(
          "POST",
          `${BASE_URL}/v1/public_site/public_orders`,
          {
            public_order: {
              total_amount: totalAmount,
              payment_method: "razorpay",
              selected_item_ids: selectedItemIds
            },
          },
          { Authorization: currentUser.data.token }
        );

        const orderData = orderResponse.data;
        console.log('Payment - Backend order response:', orderData);
        console.log('Payment - Order data from backend:', orderData);
        console.log('Payment - Amount being sent to Razorpay:', orderData.amount * 100);
        
        const options = {
          key: RAZORPAY_KEY,
          amount: orderData.amount * 100,
          currency: "INR",
          name: "JMW",
          description: "Purchase",
          order_id: orderData.razorpay_order_id,
          image: "https://cdn.razorpay.com/logos/7K3b6d18wHwKzL_medium.png",
          handler: async function (response) {
            console.log('Razorpay payment response:', response);
            
            // Verify payment status with backend
            const paymentVerified = await verifyPaymentStatus(
              response.razorpay_payment_id,
              response.razorpay_order_id,
              orderData.order_id
            );
            
            if (!paymentVerified) {
              // If verification fails, show pending message
              toast.info("Payment is being processed. You will receive confirmation shortly.");
              setTimeout(() => {
                clearCartAndRedirect();
              }, 3000);
            }
          },
          prefill: {
            name: formData ? (formData.firstName + " " + formData.lastName) : "",
            email: formData ? formData.email : "",
            contact: formData ? formData.phone : "",
          },
          notes: {
            address: formData ? (formData.shippingAddressLine1 + ", " + formData.shippingCity) : "",
          },
          theme: { color: "#3399cc" },
        };

        // Load Razorpay dynamically
        try {
          const Razorpay = await loadRazorpay();
          const rzp = new Razorpay(options);
          rzp.open();
          
          rzp.on("payment.failed", function (response) {
            console.log('Razorpay payment failed:', response);
            toast.error("Payment Failed! Please try again.");
            setLoading(false);
          });
          
          rzp.on("payment.cancelled", function (response) {
            console.log('Razorpay payment cancelled:', response);
            toast.info("Payment was cancelled.");
            setLoading(false);
          });
        } catch (error) {
          console.error("Error loading Razorpay:", error);
          toast.error("Payment gateway failed to load. Please try again.");
          setLoading(false);
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="payment-container">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      {cartLoading && (
        <div className="loading-message">
          <p>Loading your cart data...</p>
        </div>
      )}
      
      {!cartLoading && cartItems.length > 0 && (
        <OrderItemsSummary cartItems={cartItems} />
      )}
      
      <div className="payment-header">
        <h2>Payment Methods</h2>
        <p>Select your preferred payment method</p>
      </div>

      <div className="payment-methods">
        {paymentMethods.map((method) => (
          <div
            key={method.id}
            onClick={() => setSelectedMethod(method.id)}
            className={`payment-method ${
              selectedMethod === method.id ? "selected" : ""
            }`}
          >
            <div className="method-icon">{method.icon}</div>
            <div className="method-details">
              <h3>{method.name}</h3>
              <p>{method.description}</p>
            </div>
            <div className="method-selector">
              {selectedMethod === method.id ? (
                <div className="selected-indicator">
                  <FaCheck />
                </div>
              ) : (
                <div className="unselected-indicator"></div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="payment-footer">
        <button
          disabled={!selectedMethod || loading}
          className={`checkout-button ${!selectedMethod ? "disabled" : ""}`}
          onClick={handlePayment}
        >
          {loading ? "Processing..." : "Check out"}
        </button>
      </div>
    </div>
  );
};

export default Payment;